import Modal from '@components/common/modal'
import Spinner from '@components/common/spinner'
import {
  ClassRescheduleOptionsEnum,
  RescheduleModalStatesEnum,
} from '@lib/constants/reschedule-class'
import { useEffect, useMemo, useState } from 'react'
import Selection from '@components/reschedule-course-class/selection'
import ConfirmNextClassTime from '@components/reschedule-course-class/confirm-next-class-time'
import RescheduleSuccess from '@components/reschedule-course-class/reschedule-success'
import { ClassDetailsProps, StudentProps } from '@lib/types/classroom'
import NoSlotsAvailable from '@components/reschedule-course-class/no-slots-available'
import ContactRm from '@components/reschedule-course-class/contact-rm'
import { CClient, showErrorAlert } from '@common/utils'
import { useSessionContext } from '@contexts/session'
import { captureException } from '@sentry/node'
import { First8Type } from '@components/common/first-8-badge'
import date, { SYSTEM_TIMEZONE } from '@lib/utils/date.utils'
import AvailableSlots from '@components/trial-class/available-slots'
import Router from 'next/router'
import {
  flattenErrorsDictFromResponse,
  pollCeleryTaskStatus,
} from '@lib/utils/common'
import Polling from '@components/reschedule-course-class/polling'
import { handleReschdeuleAtSelectedTime } from '@lib/utils/reschedule'
import { RelationshipManagerType } from '@lib/types/common'
import CheckIfStudentIsInformed from '@components/reschedule-course-class/check-if-student-is-informed'
import RequestTeacherToInformStudent from '@components/reschedule-course-class/request-teacher-to-inform-student'
import RescheduleNotAllowed from '@components/reschedule-course-class/reschedule-not-allowed'
import { ClassScheduleType } from '@lib/types/reschedule'
import { AlertCircle, X } from 'react-feather'
import { Dayjs } from 'dayjs'
import dynamic from 'next/dynamic'

const SelectRescheduleDateTime = dynamic(
  () =>
    import('@components/reschedule-course-class/select-reschedule-datetime'),
  {
    loading: () => (
      <div className="flex items-center justify-center h-40">
        <Spinner classes="text-orange h-10 w-10" />
      </div>
    ),
  }
)

type Props = {
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  lessonNumber?: number | string
  lessonTitle: string
  teacherCalendarId: number
  batchId: string
  classroomId: number | string
  classStartTime: string
  relationshipManager: RelationshipManagerType
  studentDetails?: StudentProps
  isRescheduleRestricted?: boolean
  restrictionMessage?: string
  first8?: First8Type | null
}

type RescheduleRange = {
  min: Dayjs
  max: Dayjs
}

const ONE_HOUR_IN_MS = 60 * 60 * 1000
const URGENT_RESCHEDULE_MESSAGE =
  "If you need to reschedule urgently (within an hour of the class), please contact your teacher directly. However, we recommend avoiding last-minute changes, as they affect your teacher's availability."

/**
 * UI states:
 * 1. Loading
 * 2. Selction
 * 3. Slot selection
 * 4. No slots available
 * 5. Confirm schedule to next class
 * 6. Slot booked success
 * 7. Contact RM
 * 8. Check if student is informed (only for teachers)
 * 9: Request teacher to inform student (only for teachers)
 *
 * Flow chart: https://app.diagrams.net/#G1uW3ssVYBWgPMwbDTrK9CS7phU4NuHCDu
 */
const RescheduleClassModal = ({
  isOpen,
  setIsOpen,
  lessonNumber,
  lessonTitle,
  teacherCalendarId,
  batchId,
  classroomId,
  relationshipManager,
  studentDetails,
  classStartTime,
  isRescheduleRestricted = false,
  restrictionMessage,
  first8,
}: Props) => {
  const {
    timezone,
    teacher,
    id: currentUserId,
    modLoginUserId,
  } = useSessionContext()

  const isTeacher = useMemo(() => !!teacher, [])

  const [selectedTimezone, setSelectedTimezone] = useState(
    timezone || SYSTEM_TIMEZONE
  )
  const [activeScreen, setActiveScreen] = useState<RescheduleModalStatesEnum>(
    RescheduleModalStatesEnum.loading
  )
  const [selectedRescheduleOption, setSelectedRescheduleOption] = useState<
    ClassRescheduleOptionsEnum
  >(null)
  const [rescheduleRequestedBy, setRescheduleRequestedBy] = useState<
    'student' | 'teacher' | null
  >(null)
  const [nextClassTimestamp, setNextClassTimestamp] = useState<string>(null)
  const [upcomingClassesSchedule, setUpcomingClassesSchedule] = useState<
    ClassScheduleType[]
  >([])
  const [isLoading, setIsLoading] = useState(false)
  const [availableSlots, setAvailableSlots] = useState(null)
  const [selectedDate, setSelectedDate] = useState<string>(null)
  const [selectedTime, setSelectedTime] = useState<string>(null)
  const [
    inlineScheduleBeforeNextTimeError,
    setInlineScheduleBeforeNextTimeError,
  ] = useState<string>('')
  const restrictionCopy = restrictionMessage || URGENT_RESCHEDULE_MESSAGE
  const [rescheduleNotAllowedReason, setRescheduleNotAllowedReason] = useState<
    string
  >('')

  const [allowedRescheduleRange, setAllowedRescheduleRange] = useState<
    RescheduleRange
  >({ min: null, max: null })

  const [
    nextClassTimestampInBatchSchedule,
    setNextClassTimestampInBatchSchedule,
  ] = useState<string>(null)

  const handleRescheduleOptionSubmission = () =>
    selectedRescheduleOption === ClassRescheduleOptionsEnum.nextTime
      ? setActiveScreen(
          RescheduleModalStatesEnum.confirmRescheduleAtNextClassTime
        )
      : isTeacher
      ? getAllowedRescheduleRange()
      : getAvailableSlots()

  const handleRescheduleToNextClassTime = async () => {
    if (isLoading) return
    setIsLoading(true)
    try {
      const response = await CClient(`/api/v1/batches/${batchId}/reschedule/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ classroomId, rescheduleRequestedBy }),
      })
      if (!response.ok) {
        const errorMessage = await flattenErrorsDictFromResponse(response)
        // 400s carry a message meant for the user, e.g. the "Student requested"
        // cooldown, so show it instead of the generic error.
        if (response.status === 400) {
          showErrorAlert({ text: errorMessage })
          setIsLoading(false)
          return
        }
        throw new Error(errorMessage)
      }

      const data: { taskId: string } = await response.json()
      setActiveScreen(RescheduleModalStatesEnum.polling)
      const isPollingCompletedSuccessfully = await pollCeleryTaskStatus(
        data.taskId
      )
      if (!isPollingCompletedSuccessfully) {
        setActiveScreen(
          RescheduleModalStatesEnum.confirmRescheduleAtNextClassTime
        )
        throw new Error(`Polling for celery task id ${data.taskId} failed`)
      }
      setActiveScreen(RescheduleModalStatesEnum.success)
    } catch (error) {
      captureException(error)
      showErrorAlert({
        text:
          'Something went wrong while rescheduling your class. Please refresh the page and try again!',
      })
    }
    setIsLoading(false)
  }

  const getNextClassDetails = async () => {
    setActiveScreen(RescheduleModalStatesEnum.loading)
    try {
      const response = await CClient(
        `/api/v1/classrooms/?state=Upcoming&is_cancelled=False&order_by=Start&limit=1&batch_id=${batchId}&after_timestamp=${classStartTime}&classroom_id=${classroomId}&skip_teacher_filter=true`
      )
      if (!response.ok)
        throw new Error(await flattenErrorsDictFromResponse(response))

      const data: ClassDetailsProps[] = await response.json()
      if (data.length === 0) {
        isTeacher
          ? await getAllowedRescheduleRange()
          : await getAvailableSlots()
        return
      }
      setNextClassTimestamp(data[0].start)

      // Get the time the class moves to from the backend, which applies the
      // same rule as rescheduling: the next class time if it falls on a slot of
      // the batch schedule, else the first slot at or after it.
      const batch_id = data[0].batch.id
      const resp = await CClient(
        `/api/v1/batches/${batch_id}/next-class-time/?next_class_start=${encodeURIComponent(
          data[0].start
        )}`
      )
      if (!resp.ok) throw new Error(await flattenErrorsDictFromResponse(resp))
      const { nextClassTimeInSchedule } = await resp.json()
      if (!nextClassTimeInSchedule)
        throw new Error(`No slots found in the schedule of batch ${batch_id}`)
      setNextClassTimestampInBatchSchedule(nextClassTimeInSchedule)

      setActiveScreen(RescheduleModalStatesEnum.rescheduleOptionSelection)
    } catch (error) {
      captureException(error)
      showErrorAlert({
        text:
          'Something went wrong while fetching next class details. Please try again!',
      })
    }
  }

  /** gets previous class's details if previous class is available */
  const getPreviousClassDetails = async () => {
    try {
      setActiveScreen(RescheduleModalStatesEnum.loading)
      const response = await CClient(
        `/api/v1/classrooms/?state=Upcoming&is_cancelled=False&order_by=End&batch_id=${batchId}&before_timestamp=${classStartTime}&is_trial=False&is_adhoc=False&limit=1&classroom_id=${classroomId}&skip_teacher_filter=true`
      )

      if (!response.ok)
        throw new Error(await flattenErrorsDictFromResponse(response))

      const data: ClassDetailsProps[] = await response.json()
      return data.length === 0 ? null : data[0]
    } catch (error) {
      captureException(error)
      showErrorAlert({
        text:
          'Something went wrong while fetching previous class details. Please try again!',
      })
    }
  }

  const getAvailableSlots = async () => {
    if (isLoading) return
    setIsLoading(true)

    const previousClassDetails = await getPreviousClassDetails()
    const previousClassTimestamp = previousClassDetails
      ? previousClassDetails.start
      : date().toISOString()

    try {
      // `previousClassTimestamp` is current time if the class does not exists
      const start = date(previousClassTimestamp)
        .add(15, 'minutes')
        .toISOString()
      // If current class is last class OR next class is after 7+ days then set
      // end to be today + 7 days else set next class's start
      const end =
        !nextClassTimestamp ||
        date(nextClassTimestamp)
          .startOf('day')
          .diff(date().startOf('day'), 'days') > 7
          ? date(start).startOf('day').add(7, 'days').toISOString()
          : date(nextClassTimestamp).startOf('day').toISOString()

      const response = await CClient(
        `/api/v1/calendars/${teacherCalendarId}/slots/?start=${start}&end=${end}&event_type=Available slot&is_cancelled=false&is_expandable=true`
      )
      if (!response.ok)
        throw new Error(await flattenErrorsDictFromResponse(response))

      const data = await response.json()

      if (data.length === 0) {
        setActiveScreen(
          nextClassTimestamp
            ? RescheduleModalStatesEnum.noSlotsAvailable
            : RescheduleModalStatesEnum.contactRM
        )
        setIsLoading(false)
        return
      }

      const slots = {}
      data.forEach((slot: any) => {
        const startDate = date(slot.start)
          .tz(selectedTimezone)
          .format('DD-MM-YYYY')

        // eslint-disable-next-line no-prototype-builtins
        if (!slots.hasOwnProperty(startDate)) {
          slots[startDate] = []
        }
        slots[startDate].push(date(slot.start).tz(selectedTimezone).format())
      })

      setSelectedDate(Object.keys(slots)[0])
      setAvailableSlots(slots)
      setActiveScreen(RescheduleModalStatesEnum.availableSlots)
    } catch (error) {
      captureException(error)
      showErrorAlert({
        text:
          'Something went wrong while fetching available slots. Please try again!',
      })
    }

    setIsLoading(false)
  }

  const handleReschdeuleBeforeNextClass = async () => {
    const onSuccess = () => {
      setActiveScreen(RescheduleModalStatesEnum.success)
      if (teacher)
        setUpcomingClassesSchedule([
          {
            lessonNumber,
            lessonTitle,
            startTime: selectedTime,
            studentName: studentDetails.name,
          },
        ])
    }
    const on404 = () => {
      setSelectedTime(null)
      isTeacher ? getAllowedRescheduleRange() : getAvailableSlots()
      showErrorAlert({
        text:
          'Sorry! The time slot you selected is no longer available. Please select another slot.',
      })
    }
    handleReschdeuleAtSelectedTime({
      classroomId,
      isLoading,
      selectedDate,
      selectedTime,
      setInlineError: setInlineScheduleBeforeNextTimeError,
      setIsLoading,
      onSuccess,
      isStudent: rescheduleRequestedBy
        ? rescheduleRequestedBy === 'student'
        : true,
      on404,
      isTeacher: isTeacher,
    })
  }

  const getUpcomingClassesSchedule = async () => {
    setIsLoading(true)
    try {
      const response = await CClient(
        `/api/v1/classrooms/?state=Upcoming&is_cancelled=False&order_by=Start&limit=3&batch_id=${batchId}&classroom_id=${classroomId}&skip_teacher_filter=true`
      )
      if (!response.ok)
        throw new Error(`${response.status}: ${response.statusText}`)
      const data: ClassDetailsProps[] = await response.json()
      setUpcomingClassesSchedule(
        data.map(({ start, lesson, lessonNumber, batch }) => ({
          lessonNumber,
          lessonTitle: lesson?.title,
          startTime: start,
          studentName: batch?.students?.[0]?.name,
        }))
      )
    } catch (error) {
      captureException(error)
      showErrorAlert(
        {
          text:
            'The class is rescheduled but something went wrong while fetching upcoming classes details.',
        },
        () => Router.reload()
      )
    }
    setIsLoading(false)
  }

  const checkForOngoingClass = async () => {
    try {
      const response = await CClient(
        `/api/v1/classrooms/?state=Ongoing&is_cancelled=False&batch_id=${batchId}&limit=1`
      )
      if (!response.ok)
        throw new Error(await flattenErrorsDictFromResponse(response))

      const data: ClassDetailsProps[] = await response.json()

      if (data.length > 0) {
        setRescheduleNotAllowedReason(
          teacher
            ? 'This Batch has an ongoing class. Please try rescheduling after the ongoing class ends.'
            : 'Rescheduling is not allowed during an ongoing class. Please try after the class ends.'
        )
        return true
      }

      setRescheduleNotAllowedReason('')
      return false
    } catch (error) {
      captureException(error)
      showErrorAlert({
        text:
          'Something went wrong while checking for ongoing classes. Please try again!. If Issue persists, contact customer support.',
      })
      throw error
    }
  }

  const checkIfRescheduleAllowed = async () => {
    const parsedModLoginUserId = Number(modLoginUserId)
    if (
      modLoginUserId &&
      !isNaN(parsedModLoginUserId) &&
      parsedModLoginUserId !== currentUserId
    ) {
      setRescheduleNotAllowedReason(
        'Class rescheduling is not available when logged in using moderator privileges.'
      )
      setActiveScreen(RescheduleModalStatesEnum.rescheduleNotAllowed)
      return
    }

    setActiveScreen(RescheduleModalStatesEnum.loading)

    try {
      const hasOngoingClass = await checkForOngoingClass()
      if (hasOngoingClass) {
        setActiveScreen(RescheduleModalStatesEnum.rescheduleNotAllowed)
        return
      }
    } catch (error) {
      setIsOpen(false)
      return
    }

    setRescheduleNotAllowedReason('')

    teacher
      ? setActiveScreen(RescheduleModalStatesEnum.checkIfStudentIsInformed)
      : getNextClassDetails()
  }

  const getAllowedRescheduleRange = async () => {
    const previousClassDetails = await getPreviousClassDetails()

    // if current time is 2:26 PM, then add 10 mins to make it 2:36 PM
    // then add 15 - (36 % 15) = 15 - 6 = 9 mins to make it 2:45 PM
    const now = date().tz(selectedTimezone).add(10, 'minutes')
    const minutes = now.minute()
    const minutesToAdd = 15 - (minutes % 15)
    const roundedTime = now.add(minutesToAdd, 'minutes')

    const start = previousClassDetails
      ? date(previousClassDetails.start).tz(selectedTimezone).add(1, 'hours')
      : roundedTime
    // If current class is last class OR next class is after 7+ days then set
    // end to be today + 7 days else set next class's start
    const end = !nextClassTimestamp
      ? start.startOf('day').add(7, 'days')
      : date(nextClassTimestamp).tz(selectedTimezone).subtract(15)

    setAllowedRescheduleRange({ min: start, max: end })
    setSelectedDate(start.format('DD-MM-YYYY'))
    setSelectedTime(start.format())
    setActiveScreen(RescheduleModalStatesEnum.selectRescheduleDateTime)
  }

  useEffect(() => {
    if (activeScreen === RescheduleModalStatesEnum.success)
      getUpcomingClassesSchedule()
  }, [activeScreen])

  const isWithinTwoHours = () => {
    if (!classStartTime) {
      return false
    }
    const classStart = new Date(classStartTime).getTime()
    if (Number.isNaN(classStart)) {
      return false
    }
    const timeUntilStart = classStart - Date.now()
    return timeUntilStart >= 0 && timeUntilStart <= ONE_HOUR_IN_MS
  }

  useEffect(() => {
    if (isOpen) {
      // Short-circuit when class starts within two hours so the modal echoes
      // the same restriction copy as the dashboard before loading slots.
      if (!isTeacher && isWithinTwoHours()) {
        setRescheduleNotAllowedReason(URGENT_RESCHEDULE_MESSAGE)
        setActiveScreen(RescheduleModalStatesEnum.rescheduleNotAllowed)
        return
      }
      if (isRescheduleRestricted) {
        setRescheduleNotAllowedReason(restrictionCopy)
        setActiveScreen(RescheduleModalStatesEnum.rescheduleNotAllowed)
        return
      }
      // Check if moderator login is active and validate
      checkIfRescheduleAllowed()
    } else {
      setActiveScreen(RescheduleModalStatesEnum.loading)
      setSelectedRescheduleOption(null)
      setSelectedDate(null)
      setSelectedTime(null)
      setRescheduleRequestedBy(null)
      setRescheduleNotAllowedReason('')
    }
  }, [
    isOpen,
    currentUserId,
    isRescheduleRestricted,
    restrictionCopy,
    classStartTime,
  ])

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      shouldCloseOnEsc
      width={
        activeScreen === RescheduleModalStatesEnum.availableSlots
          ? 'max-w-2xl'
          : activeScreen === RescheduleModalStatesEnum.success && isTeacher
          ? 'max-w-md'
          : 'max-w-lg'
      }
    >
      <>
        <div className="relative flex justify-end">
          <X
            className="absolute top-0 right-0 w-5 -mt-5 -mr-5 cursor-pointer text-grey-800"
            onClick={() => setIsOpen(false)}
          />
        </div>
        {isTeacher && first8?.within && (
          <div className="mb-3 flex gap-3 rounded-lg border border-violet-100 bg-violet-50 p-4">
            <AlertCircle
              size={20}
              className="mt-0.5 flex-shrink-0 text-violet-100"
            />
            <div className="text-sm text-grey-800">
              <p className="font-600 text-grey-900">
                {first8.classNumber != null
                  ? `This is class ${first8.classNumber} of the student's first 8 classes with you.`
                  : "This is one of the student's first 8 classes with you."}
              </p>
              <p className="mt-1">
                Nearly half of students who drop off leave in their first 8
                classes, so please keep the original time if you can.
              </p>
            </div>
          </div>
        )}
        <div
          className="overflow-auto thin-scroll-bar"
          style={{ maxHeight: '80vh' }}
        >
          {activeScreen === RescheduleModalStatesEnum.loading && (
            <div className="flex items-center justify-center h-64">
              <Spinner classes="text-orange h-10 w-10" />
            </div>
          )}
          {activeScreen === RescheduleModalStatesEnum.rescheduleNotAllowed && (
            <RescheduleNotAllowed
              message={rescheduleNotAllowedReason}
              onClose={() => setIsOpen(false)}
            />
          )}
          {activeScreen ===
            RescheduleModalStatesEnum.checkIfStudentIsInformed && (
            <CheckIfStudentIsInformed
              onNoClick={() =>
                setActiveScreen(
                  RescheduleModalStatesEnum.requestTeacherToInformStudent
                )
              }
              onYesClick={() => getNextClassDetails()}
              start={classStartTime}
              lessonNumber={lessonNumber}
              lessonTitle={lessonTitle}
              rescheduleRequestedBy={rescheduleRequestedBy}
              setRescheduleRequestedBy={setRescheduleRequestedBy}
            />
          )}
          {activeScreen ===
            RescheduleModalStatesEnum.requestTeacherToInformStudent && (
            <RequestTeacherToInformStudent
              onOkClick={() => setIsOpen(false)}
              start={classStartTime}
              studentDetails={studentDetails}
              lessonNumber={lessonNumber}
              lessonTitle={lessonTitle}
            />
          )}
          {activeScreen ===
            RescheduleModalStatesEnum.rescheduleOptionSelection && (
            <Selection
              rescheduleRequestedBy={rescheduleRequestedBy}
              isWithinFirst8={!!first8?.within}
              selectedRescheduleOption={selectedRescheduleOption}
              setSelectedRescheduleOption={setSelectedRescheduleOption}
              lessonNumber={lessonNumber}
              lessonTitle={lessonTitle}
              onContinue={handleRescheduleOptionSubmission}
              onGoBack={() => setIsOpen(false)}
              timezone={selectedTimezone}
              isLoading={isLoading}
              isTeacher={isTeacher}
              nextClassTimestampInBatchSchedule={
                nextClassTimestampInBatchSchedule
              }
            />
          )}
          {activeScreen ===
            RescheduleModalStatesEnum.confirmRescheduleAtNextClassTime && (
            <ConfirmNextClassTime
              lessonNumber={lessonNumber}
              lessonTitle={lessonTitle}
              onConfirm={handleRescheduleToNextClassTime}
              onGoBack={() =>
                setActiveScreen(
                  RescheduleModalStatesEnum.rescheduleOptionSelection
                )
              }
              timezone={selectedTimezone}
              isLoading={isLoading}
              isTeacher={isTeacher}
              rescheduleRequestedBy={rescheduleRequestedBy}
              nextClassTimestampInBatchSchedule={
                nextClassTimestampInBatchSchedule
              }
            />
          )}
          {activeScreen === RescheduleModalStatesEnum.polling && <Polling />}
          {activeScreen === RescheduleModalStatesEnum.availableSlots && (
            <AvailableSlots
              isBooking={isLoading}
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              selectedTimezone={selectedTimezone}
              setSelectedDate={setSelectedDate}
              setSelectedTime={setSelectedTime}
              setSelectedTimezone={setSelectedTimezone}
              slots={availableSlots}
              onBook={handleReschdeuleBeforeNextClass}
              title={`You are rescheduling Lesson ${lessonNumber}: ${lessonTitle}`}
              timezoneAlertBgColor="yellow"
              onGoBack={
                nextClassTimestamp
                  ? () =>
                      setActiveScreen(
                        RescheduleModalStatesEnum.rescheduleOptionSelection
                      )
                  : null
              }
              ctaText="Confirm rescheduling"
              error={inlineScheduleBeforeNextTimeError}
            />
          )}
          {activeScreen ===
            RescheduleModalStatesEnum.selectRescheduleDateTime && (
            <SelectRescheduleDateTime
              selectedTimezone={selectedTimezone}
              selectedTime={selectedTime}
              setSelectedTimezone={setSelectedTimezone}
              setSelectedDate={setSelectedDate}
              setSelectedTime={setSelectedTime}
              title={`You are rescheduling Lesson: ${lessonTitle}`}
              isBooking={isLoading}
              onConfirm={handleReschdeuleBeforeNextClass}
              allowedRescheduleRange={allowedRescheduleRange}
              onGoBack={
                nextClassTimestamp
                  ? () =>
                      setActiveScreen(
                        RescheduleModalStatesEnum.rescheduleOptionSelection
                      )
                  : () => setIsOpen(false)
              }
            />
          )}
          {activeScreen === RescheduleModalStatesEnum.contactRM && (
            <ContactRm
              lessonNumber={lessonNumber}
              lessonTitle={lessonTitle}
              onGoBack={() => setIsOpen(false)}
              relationshipManager={relationshipManager}
              isTeacher={isTeacher}
            />
          )}
          {activeScreen === RescheduleModalStatesEnum.noSlotsAvailable && (
            <NoSlotsAvailable
              lessonNumber={lessonNumber}
              lessonTitle={lessonTitle}
              onAttendClassNextTime={() =>
                setActiveScreen(
                  RescheduleModalStatesEnum.confirmRescheduleAtNextClassTime
                )
              }
              onGoBack={() => setIsOpen(false)}
              selectedTimezone={selectedTimezone}
              nextClassTimestamp={nextClassTimestamp}
              isTeacher={isTeacher}
            />
          )}
          {activeScreen === RescheduleModalStatesEnum.success && (
            <RescheduleSuccess
              scheduleOfUpcomingClasses={upcomingClassesSchedule}
              isLoading={isLoading}
              isTeacher={isTeacher}
              selectedTimezone={selectedTimezone}
              rescheduleRequestedBy={rescheduleRequestedBy}
            />
          )}
        </div>
      </>
    </Modal>
  )
}

export default RescheduleClassModal
